import {Optional} from "../../../js/util/Optional";
import {ascending, distinct} from "../../../js/util/UtilFunctions";
import {OptionType} from "../../../types/ChainService";
import {HistoricalOptionRecord} from "../../price/historical/HistoricalPriceModels";
import {ExpiryType} from "../../expiry/ExpiryService";
import {OptionPair} from "./OptionPair";
import {ArrayUtil} from "../../../js/util/ArrayUtil";

export class OptionExpiry {

    public readonly puts: Array<HistoricalOptionRecord>;
    public readonly calls: Array<HistoricalOptionRecord>;
    private readonly options = new Map<string, HistoricalOptionRecord>();
    private readonly sortedStrikes: Array<number>;
    private readonly optionsByStrike: Map<number, OptionPair>;

    public constructor(public readonly date: string,
                       public readonly expiryType: ExpiryType,
                       options: Array<HistoricalOptionRecord>) {
        options.forEach(o => this.options.set(o.code, o));
        this.sortedStrikes = options.map(o => o.strike).filter(distinct).sort(ascending);
        this.optionsByStrike = this.groupByStrike(this.sortedStrikes, options);
        this.puts = [];
        this.calls = [];
        this.sortedStrikes.forEach((strike) => {
            const pair = this.optionsByStrike.get(strike)!;
            this.puts.push(pair.put);
            this.calls.push(pair.call);
        });
    }

    private groupByStrike(strikes: Array<number>,
                          options: Array<HistoricalOptionRecord>): Map<number, OptionPair> {
        const puts = options.filter(o => o.optionType === OptionType.PUT);
        const calls = options.filter(o => o.optionType === OptionType.CALL);

        const result = new Map<number, OptionPair>();
        strikes.forEach(strike => {
            const put = puts.find(p => p.strike === strike)!;
            const call = calls.find(c => c.strike === strike)!;
            const pair = OptionPair.of(put, call);
            result.set(strike, pair);
        });
        return result;
    }

    public get(code: string): Optional<HistoricalOptionRecord>;
    public get(strike: number, type: OptionType): Optional<HistoricalOptionRecord>;

    public get(codeOrStrike: string | number, type?: OptionType): Optional<HistoricalOptionRecord> {
        if (typeof codeOrStrike === "string") {
            return Optional.of(codeOrStrike).map(c => this.options.get(c));
        } else if (type === OptionType.PUT) {
            return this.forStrike(codeOrStrike).map(p => p.put);
        } else {
            return this.forStrike(codeOrStrike).map(p => p.call);
        }
    }

    public get strikes(): Array<number> {
        return this.sortedStrikes;
    }

    public forStrike(strike: number): Optional<OptionPair> {
        return Optional.of(strike).map(s => this.optionsByStrike.get(s));
    }

    public closestToStrike(strike: number): Optional<OptionPair> {
        if (this.optionsByStrike.has(strike)) {
            return Optional.of(this.optionsByStrike.get(strike));
        } else {
            return this.closesStrikeTo(strike)
                       .map((str) => this.optionsByStrike.get(str));
        }
    }

    private closesStrikeTo(strike: number): Optional<number> {
        const belowStrike = this.sortedStrikes.filter((str) => str < strike);
        const aboveStrike = this.sortedStrikes.filter((str) => str > strike);
        const closestBelow = ArrayUtil.max(belowStrike, (a, b) => a - b);
        const closestAbove = ArrayUtil.min(aboveStrike, (a, b) => a - b);

        if (closestAbove.isEmpty() && closestBelow.isEmpty()) {
            return Optional.empty();
        } else if (closestAbove.isEmpty()) {
            return closestBelow;
        } else if (closestBelow.isEmpty()) {
            return closestAbove;
        } else {
            return Optional.zip(closestAbove, closestBelow)
                           .map(([above, below]) => {
                               const aboveDiff = above - strike;
                               const belowDiff = strike - below;
                               if (aboveDiff < belowDiff) {
                                   return above;
                               } else {
                                   return below;
                               }
                           });
        }
    }

    public nextStrike(code: string): Optional<HistoricalOptionRecord> {
        const maybeRecord = this.get(code);
        if (maybeRecord.isEmpty()) {
            return Optional.empty();
        }
        const record = maybeRecord.get();

        return Optional.of(record)
                       .map((r) => r.strike)
                       .map((strike) => this.sortedStrikes.indexOf(strike))
                       .map((idx) => idx + 1)
                       .map((idx) => this.sortedStrikes[idx])
                       .map((str) => this.optionsByStrike.get(str))
                       .map((pair) => pair.get(record.optionType));
    }

    public previousStrike(code: string): Optional<HistoricalOptionRecord> {
        const maybeRecord = this.get(code);
        if (maybeRecord.isEmpty()) {
            return Optional.empty();
        }
        const record = maybeRecord.get();

        return Optional.of(record)
                       .map((r) => r.strike)
                       .map((strike) => this.sortedStrikes.indexOf(strike))
                       .map((idx) => idx - 1)
                       .map((idx) => this.sortedStrikes[idx])
                       .map((str) => this.optionsByStrike.get(str))
                       .map((pair) => pair.get(record.optionType));
    }
}
